import React, { useState, useRef, useEffect } from "react";
import { useClickOutside } from "../../../hooks/useClickOutside";
import "./NewFolderPopover.css";

interface NewFolderPopoverProps {
  isOpen: boolean;
  anchorRef: React.RefObject<HTMLButtonElement | null>;
  onClose: () => void;
  onConfirm: (name: string) => void;
}

const NewFolderPopover: React.FC<NewFolderPopoverProps> = ({
  isOpen,
  anchorRef,
  onClose,
  onConfirm,
}) => {
  const [folderName, setFolderName] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      setFolderName("");
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  useClickOutside([popoverRef, anchorRef], onClose, isOpen);

  const getStyle = (): React.CSSProperties => {
    if (!anchorRef.current) return {};
    const rect = anchorRef.current.getBoundingClientRect();
    return {
      top: rect.top,
      left: rect.right + 12,
    };
  };

  const handleConfirm = () => {
    const trimmed = folderName.trim();
    if (!trimmed) return;
    onConfirm(trimmed);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div ref={popoverRef} className="new-folder-popover" style={getStyle()}>
      <div className="new-folder-popover-header">
        <div className="new-folder-popover-icon">
          <i className="fas fa-folder-plus" />
        </div>
        <div>
          <p className="new-folder-popover-title">New folder</p>
          <p className="new-folder-popover-subtitle">Organise your chats into a folder</p>
        </div>
        <button className="new-folder-popover-close" onClick={onClose}>
          <i className="fas fa-times" />
        </button>
      </div>

      <input
        ref={inputRef}
        className="new-folder-popover-input"
        placeholder="Folder name..."
        value={folderName}
        onChange={(e) => setFolderName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") handleConfirm();
          if (e.key === "Escape") onClose();
        }}
        maxLength={80}
      />

      <div className="new-folder-popover-actions">
        <button className="new-folder-popover-btn cancel" onClick={onClose}>
          Cancel
        </button>
        <button
          className="new-folder-popover-btn confirm"
          onClick={handleConfirm}
          disabled={!folderName.trim()}
        >
          Create folder
        </button>
      </div>
    </div>
  );
};

export default NewFolderPopover;